import React, { useState } from 'react';
import { FileText, Clock, Layers, MoreVertical, Trash2, Edit, Download, Eye, Search, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { mockPresentations, mockGeneratedSlides, presentationTemplates } from '../data/mockData';
import PresentationPreview from './PresentationPreview';
import { toast } from '../hooks/use-toast';

const PresentationsList = () => {
  const [presentations, setPresentations] = useState(mockPresentations);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPresentation, setSelectedPresentation] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const filteredPresentations = presentations.filter(p => 
    p.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleDelete = (id) => {
    setPresentations(prev => prev.filter(p => p.id !== id));
    toast({
      title: 'Presentación eliminada',
      description: 'La presentación ha sido eliminada correctamente.'
    });
  };

  const handlePreview = (presentation) => {
    setSelectedPresentation(presentation);
    setShowPreview(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Mis Presentaciones</h1>
          <p className="text-gray-500 mt-1">Gestiona y accede a tus presentaciones generadas</p>
        </div>
        <Link to="/">
          <Button className="bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Nueva Presentación
          </Button>
        </Link>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Buscar presentaciones..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 border-gray-300"
        />
      </div>

      {/* Presentations Grid */}
      {filteredPresentations.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="w-12 h-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay presentaciones</h3>
            <p className="text-gray-500 mb-6 text-center">
              {searchQuery 
                ? 'No se encontraron presentaciones con ese nombre.'
                : 'Crea tu primera presentación con IA para comenzar.'
              }
            </p>
            {!searchQuery && (
              <Link to="/">
                <Button className="bg-teal-600 hover:bg-teal-700 text-white">
                  Crear Presentación
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPresentations.map((presentation) => (
            <Card 
              key={presentation.id}
              className="border-gray-200 hover:shadow-md transition-shadow cursor-pointer group"
            >
              <CardContent className="p-0">
                {/* Thumbnail Preview */}
                <div 
                  className="h-40 bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center relative overflow-hidden"
                  onClick={() => handlePreview(presentation)}
                >
                  <div className="absolute top-0 left-0 right-0 h-1 bg-teal-600" />
                  <div className="text-center p-4">
                    <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <span className="text-sm text-gray-400 font-medium">Click para ver</span>
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button 
                        variant="secondary" 
                        size="sm"
                        className="bg-white shadow-lg"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Vista Previa
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Card Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{presentation.title}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {formatDate(presentation.createdAt)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Layers className="w-3.5 h-3.5" />
                          {presentation.slidesCount} slides
                        </span>
                      </div>
                    </div>

                    {/* Actions Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handlePreview(presentation)}>
                          <Eye className="w-4 h-4 mr-2" />
                          Ver presentación
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Edit className="w-4 h-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Download className="w-4 h-4 mr-2" />
                          Descargar PPT
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => handleDelete(presentation.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && selectedPresentation && (
        <PresentationPreview
          slides={mockGeneratedSlides}
          template={presentationTemplates[0]}
          title={selectedPresentation.title}
          onClose={() => {
            setShowPreview(false);
            setSelectedPresentation(null);
          }}
        />
      )}
    </div>
  );
};

export default PresentationsList;
